/**
 * Client-safe shared internals for the community action modules.
 *
 * This module is NOT a `'use server'` module and carries NO server-only
 * transitive imports — it holds only the pure `normalizeHashtag` helper and
 * the Zod schemas reused across the post / comment / follow / organization /
 * community-event / post-tag / mention action groups.
 *
 * It MUST stay client-safe: the public barrel `./actions.ts` re-exports
 * `normalizeHashtag` from here, so a `'use client'` component importing the
 * barrel pulls this module's full graph into the browser bundle. Anything
 * that touches the Supabase server / admin clients or the notification
 * handler lives in the server-only `./community-server-internals.ts` instead
 * (imported directly by the `'use server'` leaf modules, which Next stubs as
 * RPC refs on the client).
 */

import { z } from 'zod';

/**
 * Normalize a raw hashtag URL segment to the canonical tag: lowercased, and
 * stripped to letters / marks / digits / underscore (matching the `#tag`
 * linkifier in `post-body.tsx`). Returns null for an empty result so callers
 * can short-circuit to an empty feed instead of running a wildcard query.
 *
 * Lives here (not in a `'use server'` module) so both the hashtag read action
 * and the server-component page can import this sync helper.
 */
export function normalizeHashtag(raw: string): string | null {
  const cleaned = raw
    .toLowerCase()
    .replace(/^#/, '')
    .replace(/[^\p{L}\p{M}0-9_]/gu, '');
  return cleaned.length > 0 ? cleaned : null;
}

// =====================
// Zod Schemas
// =====================

export const CreatePostSchema = z.object({
  content: z
    .string()
    .min(1, 'Post content is required')
    .max(5000, 'Post content is too long')
    .transform((v) => v.trim()),
  mediaIds: z.array(z.string().uuid('Invalid media ID')).optional().default([]),
  status: z.enum(['published', 'draft']).optional().default('published'),
  // The audition/competition wrapped by the post (`posts.vacancy_id`).
  // Optional and nullable — most posts carry none. Ownership is enforced
  // server-side by `userCanAttachAudition` before the insert.
  vacancyId: z.string().uuid('Invalid audition ID').nullish(),
});

// Composer picker: search the auditions the caller organizes, by title.
export const AuthoredAuditionsSearchSchema = z.object({
  query: z.string().max(100).optional().default(''),
  limit: z.number().int().min(1).max(20).optional().default(10),
});

export const ShareTypeSchema = z.enum([
  'copy_link',
  'twitter',
  'facebook',
  'linkedin',
  'whatsapp',
  'email',
]);

export const PostIdSchema = z.string().uuid('Invalid post ID');
export const CommentIdSchema = z.string().uuid('Invalid comment ID');
export const ProfileIdSchema = z.string().uuid('Invalid profile ID');
export const OrganizationIdSchema = z.string().uuid('Invalid organization ID');

export const CreateCommentSchema = z.object({
  postId: z.string().uuid('Invalid post ID'),
  body: z
    .string()
    .min(1, 'Comment cannot be empty')
    .max(2000, 'Comment is too long')
    .transform((v) => v.trim()),
  parentCommentId: z.string().uuid('Invalid parent comment ID').optional(),
});

// Separate schema for updating drafts - does NOT include status to prevent status manipulation
export const UpdateDraftSchema = z.object({
  content: z
    .string()
    .min(1, 'Post content is required')
    .max(5000, 'Post content is too long')
    .transform((v) => v.trim()),
  mediaIds: z.array(z.string().uuid('Invalid media ID')).optional().default([]),
});

export const OrganizationFiltersSchema = z.object({
  type: z
    .enum([
      'all',
      'school',
      'company',
      'course',
      'competition',
      'foundation',
      'brand',
      'service',
    ])
    .optional()
    .default('all'),
  reach: z
    .enum(['all', 'international', 'national', 'local'])
    .optional()
    .default('all'),
  search: z.string().optional().default(''),
});

export const CommunityEventFiltersSchema = z.object({
  eventType: z
    .enum([
      'all',
      'summerIntensive',
      'competition',
      'event',
      'performance',
      'jobFair',
      'photoshoot',
      'workshop',
      'audition',
      'masterclass',
    ])
    .optional()
    .default('all'),
  search: z.string().optional().default(''),
});

export const TagIdSchema = z.string().uuid('Invalid tag ID');

export const MentionSearchSchema = z.object({
  query: z
    .string()
    .min(1, 'Search query is required')
    .max(100, 'Search query too long')
    .transform((v) => v.trim()),
  limit: z.number().int().min(1).max(20).optional().default(10),
});

export const FeedQuerySchema = z.object({
  sortBy: z.enum(['hot', 'recent', 'following']).optional().default('hot'),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(50).optional().default(20),
});

export const OrganizationPostsQuerySchema = z.object({
  organizationId: z.string().uuid(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(50).optional().default(20),
});
